import React, { useState, useEffect, useMemo } from "react";
import { Box, Flex, Text, Button, ButtonGroup } from "@chakra-ui/react";
import { useLazyQuery } from "@apollo/client";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import ApexChart from "components/Charts/ApexChart";
import { FETCH_BLOCKS_STATS } from "utils/queries";

const BlocksStatsChart = () => {
  const [chartData, setChartData] = useState([]);
  const [duration, setDuration] = useState("day");

  // Memoize timestamps to prevent infinite re-renders
  const timestamps = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const oneDaySeconds = 24 * 60 * 60;
    const oneWeekSeconds = 7 * oneDaySeconds;
    const oneMonthSeconds = 30 * oneDaySeconds;

    switch (duration) {
      case "week":
        return {
          timestamp_lt: now,
          timestamp_gt: now - oneWeekSeconds,
          week: true,
          month: false,
        };
      case "month":
        return {
          timestamp_lt: now,
          timestamp_gt: now - oneMonthSeconds,
          week: false,
          month: true,
        };
      default:
        // day
        return {
          timestamp_lt: now,
          timestamp_gt: now - oneDaySeconds,
          week: false,
          month: false,
        };
    }
  }, [duration]);

  const [getBlocksStats, { data: blocksStatsData, loading, error }] =
    useLazyQuery(FETCH_BLOCKS_STATS, {
      fetchPolicy: "network-only",
    });

  useEffect(() => {
    getBlocksStats({
      variables: timestamps,
    });
  }, [duration, timestamps, getBlocksStats]);

  const transformedChartData = useMemo(() => {
    if (!blocksStatsData?.finalizeBlockEvents) return [];

    // Sort by epoch to ensure proper chronological order
    return [...blocksStatsData.finalizeBlockEvents].sort(
      (a, b) => parseInt(a.epoch) - parseInt(b.epoch)
    );
  }, [blocksStatsData]);

  useEffect(() => {
    if (transformedChartData) {
      setChartData(transformedChartData);
    }
  }, [transformedChartData]);

  // Prepare data for ApexCharts
  const chartSeries = useMemo(() => {
    return [
      {
        name: "Number of Requests",
        data: chartData.map((item) => ({
          x: parseInt(item.epoch),
          y: parseInt(item.numRequests),
          timestamp: item.timestamp,
        })),
      },
    ];
  }, [chartData]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        id: "blocks-stats-chart",
        type: "area",
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        zoom: {
          enabled: true,
          type: "x",
          autoScaleYaxis: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "vertical",
          shadeIntensity: 0.5,
          gradientToColors: ["#4FD1C5"],
          inverseColors: false,
          opacityFrom: 0.8,
          opacityTo: 0.2,
          stops: [0, 90, 100],
        },
      },
      xaxis: {
        type: "numeric",
        title: {
          text: "Epoch",
          style: {
            fontSize: "14px",
            fontWeight: 600,
          },
        },
        labels: {
          formatter: function (value) {
            return Math.floor(value);
          },
        },
        tickAmount: 10,
      },
      yaxis: {
        title: {
          text: "Number of Requests",
          style: {
            fontSize: "14px",
            fontWeight: 600,
          },
        },
        labels: {
          formatter: function (value) {
            return Math.floor(value).toLocaleString();
          },
        },
      },
      tooltip: {
        custom: function ({ seriesIndex, dataPointIndex, w }) {
          const data = w.config.series[seriesIndex].data[dataPointIndex];
          const timestamp = data.timestamp;
          const dateTime = timestamp
            ? new Date(parseInt(timestamp) * 1000).toLocaleString()
            : "";
          const epoch = data.x;
          const requests = data.y;

          return `
            <div style="padding: 12px; background: #0f1535; border: 1px solid #56577A; border-radius: 6px;">
              <div style="color: #fff; font-weight: 600; margin-bottom: 8px;">
                Epoch ${epoch}
              </div>
              <div style="color: #a0aec0; font-size: 12px; margin-bottom: 8px;">
                ${dateTime}
              </div>
              <div style="color: #4FD1C5; font-weight: 600;">
                ${requests.toLocaleString()} Requests
              </div>
            </div>
          `;
        },
      },
      markers: {
        size: 0,
        hover: {
          size: 6,
          sizeOffset: 3,
        },
      },
      grid: {
        borderColor: "#56577A",
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
        padding: {
          top: 0,
          right: 10,
          bottom: 0,
          left: 10,
        },
      },
      colors: ["#4FD1C5"],
    };
  }, []);

  if (error) {
    return (
      <Card overflowX={{ sm: "scroll", xl: "hidden" }} pb="0px">
        <CardBody>
          <Text color="red.400" textAlign="center">
            Error loading blocks statistics. Please try again later.
          </Text>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card overflowX={{ sm: "scroll", xl: "hidden" }} mb={10}>
      <CardHeader p="6px 0px 22px 0px">
        <Flex
          justify="space-between"
          align="center"
          w="100%"
          px={{ base: 3, md: 6 }}
          flexDir={{ base: "column", sm: "row" }}
          gap={{ base: 3, sm: 0 }}
        >
          <Text fontSize="lg" color="#fff" fontWeight="bold">
            Blocks Request Statistics
          </Text>
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button
              colorScheme={duration === "day" ? "blue" : "gray"}
              onClick={() => setDuration("day")}
              bg={duration === "day" ? "blue.500" : "transparent"}
              color={duration === "day" ? "white" : "gray.400"}
              _hover={{
                bg: duration === "day" ? "blue.600" : "whiteAlpha.200",
              }}
            >
              1D
            </Button>
            <Button
              colorScheme={duration === "week" ? "blue" : "gray"}
              onClick={() => setDuration("week")}
              bg={duration === "week" ? "blue.500" : "transparent"}
              color={duration === "week" ? "white" : "gray.400"}
              _hover={{
                bg: duration === "week" ? "blue.600" : "whiteAlpha.200",
              }}
            >
              1W
            </Button>
            <Button
              colorScheme={duration === "month" ? "blue" : "gray"}
              onClick={() => setDuration("month")}
              bg={duration === "month" ? "blue.500" : "transparent"}
              color={duration === "month" ? "white" : "gray.400"}
              _hover={{
                bg: duration === "month" ? "blue.600" : "whiteAlpha.200",
              }}
            >
              1M
            </Button>
          </ButtonGroup>
        </Flex>
      </CardHeader>
      <CardBody>
        <Box p={{ base: 2, md: 4 }} minH="450px" width="100%">
          {loading ? (
            <Flex
              justify="center"
              align="center"
              h="400px"
              flexDirection="column"
              gap={4}
            >
              <Text color="gray.400" fontSize="lg">
                Loading statistics...
              </Text>
            </Flex>
          ) : chartData.length === 0 ? (
            <Flex justify="center" align="center" h="400px">
              <Text color="gray.400" fontSize="lg">
                No statistics available.
              </Text>
            </Flex>
          ) : (
            <ApexChart
              type="area"
              series={chartSeries}
              options={chartOptions}
              height="400px"
            />
          )}
        </Box>
      </CardBody>
    </Card>
  );
};

export default BlocksStatsChart;
